import { apiClient } from '../config/api';

// FEAT-043-admin-web client (MVP-8.9, MVP-18.24).
//
// Privacy contract — see `docs/context/artifacts.yaml` →
// `trust_score_privacy_and_retention` for the binding rules. Every read
// here carries an admin-supplied `reason` (>= 10 chars, enforced
// client-side by the dialog and server-side by `readReasonSchema` in the
// api). Both `read` and `signals` write a `TrustScoreReadAudit` row
// server-side before any data is returned. Recompute does not require
// a reason because no score data leaves the server in that response.
//
// We reuse the shared `apiClient.post()` helper rather than raw `fetch`
// — `apiClient` already supports POST (only PATCH was missing per the
// FEAT-044 / FEAT-047 admin notes), so there is no need for a custom
// helper here.

export interface TrustScoreCategoryBreakdown {
  interaction: number | null;
  booking: number | null;
  contentIntent: number | null;
  behavioral: number | null;
  sentiment: number | null;
}

export interface TrustScoreRecord {
  id: string;
  userId: string;
  compositeScore: number | null;
  categoryScores: TrustScoreCategoryBreakdown;
  signalCount: number;
  lastComputedAt: string;
}

export interface TrustScoreReadResponse {
  score: TrustScoreRecord | null;
  audit: {
    adminId: string;
    targetUserId: string;
    reason: string;
  };
}

export interface TrustScoreSignalRow {
  id: string;
  userId: string;
  signalType:
    | 'INTERACTION_RATING'
    | 'BOOKING_RATING'
    | 'MESSAGE_HEURISTIC'
    | 'BEHAVIORAL_PATTERN'
    | 'REPORT_DISPOSITION';
  signalSubtype: string;
  value: number;
  weight: number;
  sourceRefType: string | null;
  sourceRefId: string | null;
  capturedAt: string;
}

export interface TrustScoreSignalsResponse {
  signals: TrustScoreSignalRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TrustScoreRecomputeResponse {
  score: TrustScoreRecord;
}

export interface RequestScoreReadInput {
  userId: string;
  reason: string;
}

export interface RequestSignalsReadInput {
  userId: string;
  reason: string;
  page?: number;
  limit?: number;
}

export interface TrustScoreApiError {
  status: number;
  code?: string;
  message: string;
}

function toApiError(error: unknown, fallbackMessage: string): TrustScoreApiError {
  if (error instanceof Error) {
    const match = error.message.match(/status:\s*(\d{3})/);
    const status = match ? Number(match[1]) : 0;
    return { status, message: error.message };
  }
  return { status: 0, message: fallbackMessage };
}

class TrustScoreService {
  async requestScoreRead({
    userId,
    reason,
  }: RequestScoreReadInput): Promise<TrustScoreReadResponse> {
    try {
      const response = await apiClient.post<TrustScoreReadResponse>(
        `/admin/users/${encodeURIComponent(userId)}/trust-score/read`,
        { reason },
      );
      return response.data;
    } catch (err) {
      throw toApiError(err, 'Failed to read trust score');
    }
  }

  async requestSignalsRead({
    userId,
    reason,
    page,
    limit,
  }: RequestSignalsReadInput): Promise<TrustScoreSignalsResponse> {
    try {
      const body: Record<string, unknown> = { reason };
      if (typeof page === 'number') body.page = page;
      if (typeof limit === 'number') body.limit = limit;
      const response = await apiClient.post<TrustScoreSignalsResponse>(
        `/admin/users/${encodeURIComponent(userId)}/trust-score/signals`,
        body,
      );
      return response.data;
    } catch (err) {
      throw toApiError(err, 'Failed to load trust score signals');
    }
  }

  async requestRecompute({ userId }: { userId: string }): Promise<TrustScoreRecomputeResponse> {
    try {
      const response = await apiClient.post<TrustScoreRecomputeResponse>(
        `/admin/trust-score/recompute/${encodeURIComponent(userId)}`,
      );
      return response.data;
    } catch (err) {
      throw toApiError(err, 'Failed to recompute trust score');
    }
  }
}

export const trustScoreService = new TrustScoreService();

// --- Presentation helpers ----------------------------------------------------

export type TrustScoreBand = 'healthy' | 'watch' | 'concern' | 'unknown';

export interface TrustScoreBandInfo {
  band: TrustScoreBand;
  label: string;
  description: string;
  // MUI palette colour key the UI maps to a Chip / Avatar background.
  color: 'success' | 'warning' | 'error' | 'default';
}

// Threshold map — documented in `docs/features/FEAT-043-Implementation.md`.
// >= 75 = Healthy, 50–74 = Watch, < 50 = Concern, null = Not enough signal.
// Kept conservative on purpose: a "Healthy" label is a non-trivial claim
// about a user we are about to act on.
export function describeBand(compositeScore: number | null): TrustScoreBandInfo {
  if (compositeScore == null) {
    return {
      band: 'unknown',
      label: 'Not enough signal',
      description: 'The score requires at least 5 signals before a band is assigned.',
      color: 'default',
    };
  }
  if (compositeScore >= 75) {
    return {
      band: 'healthy',
      label: 'Healthy',
      description: 'Signals are consistent with normal, low-risk activity.',
      color: 'success',
    };
  }
  if (compositeScore >= 50) {
    return {
      band: 'watch',
      label: 'Watch',
      description: 'Mixed signals — worth reviewing recent reports and message heuristics.',
      color: 'warning',
    };
  }
  return {
    band: 'concern',
    label: 'Concern',
    description: 'Signals trend negative — review raw signals before any user-facing action.',
    color: 'error',
  };
}

export const CATEGORY_DISPLAY_ORDER: Array<{
  key: keyof TrustScoreCategoryBreakdown;
  label: string;
  hint?: string;
}> = [
  { key: 'interaction', label: 'Interaction ratings' },
  { key: 'booking', label: 'Booking ratings' },
  { key: 'contentIntent', label: 'Content / intent heuristics' },
  { key: 'behavioral', label: 'Behavioral patterns' },
  {
    key: 'sentiment',
    label: 'Sentiment',
    // Sentiment is reserved for the §17 AI sentiment work
    // (MVP-17.21a / MVP-17.22a). Until then `categoryScores.sentiment`
    // is always null and we render the "Not enough signal" treatment
    // with a small "(coming soon)" hint per the panel spec.
    hint: 'coming soon',
  },
];
